import React, { ReactNode } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ITicketBoard } from '../../../../../interfaces/models/ticket.interfaces'
import { cn } from '../../../../../helpers/utils'
import { theme } from '../../../../../theme/theme'
import { formatRelative } from '../../../../../helpers/date'
import { NotificationBadge } from '../../../../../components/notifications/NotificationBadge/NotificationBadge'
import { societyQueries } from '../../../../../api/societies'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { TicketAvatar } from '../../../../../components/common/Avatar'
import { TicketBadge } from '../../../../../components/ticket/TicketBadge'
import { TicketVariantBadge } from '../../../../../components/ticket/TicketVariantBadge'
import { useAuth } from '../../../../../hooks/useAuth'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { getUserFullName } from '../../../../../helpers/user'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import {
  TicketFilterBoard,
  infiniteTicketQueries,
} from '../../../../../api/ticket'
import { ErrorView } from '../../../../error/ErrorView/ErrorView'
import { IllustrationChoices } from '../../../../../components/common/Illustration/Illustration'
import { InfiniteScroll } from '../../../../../components/common/InfiniteScroll'

const useMessage = (ticketMessage?: ITicketBoard['lastMessage']): ReactNode => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const { data: societyUsers } = useQuery(societyQueries.getSocietyUsers())

  const getSender = (): ReactNode => {
    if (ticketMessage?.authorId === userId)
      return translate('ticketListViewBoard.sender.me')
    const user = societyUsers?.find(
      (societyUser) => societyUser._id === ticketMessage?.authorId
    )
    if (!user) return translate('ticketListViewBoard.sender.deletedUser')
    return user.name
  }

  const getMessage = (): string => {
    if (ticketMessage?.message) return ticketMessage.message
    if (ticketMessage?.mediaIds.length) {
      return translate('ticketListViewBoard.message.sentImage')
    }
    if (ticketMessage?.documentIds.length) {
      return translate('ticketListViewBoard.message.sentFile')
    }
    return ''
  }
  return (
    <span>
      {getSender()}: {getMessage()}
    </span>
  )
}

export const TicketListItemBoard = ({
  ticket,
  active,
  className,
}: {
  ticket: ITicketBoard
  active?: boolean
  className?: string
}): ReactNode => {
  const message = useMessage(ticket.lastMessage)

  return (
    <Link
      to={reverseUrl('management:ticket-detail', {
        ticketId: ticket.id,
        id: ticket.societyId,
      })}
      style={theme.textVariants.base}
      className={cn(
        ' hover:bg-gray-light rounded-md px-2 py-3 w-full relative block',
        ticket.unreadMessages > 0
          ? 'bg-almostBrandGreen/10 border-brandGreen'
          : '',
        active ? 'bg-gray-light' : '',
        className
      )}
    >
      <div className="flex justify-between">
        <span
          style={theme.textVariants.caption}
          className="whitespace-nowrap text-neutral-50"
        >
          #{ticket.ticketNumber}
        </span>
        <span
          style={theme.textVariants.caption}
          className="self-end text-neutral-50"
        >
          {formatRelative(ticket.lastMessage?.createdAt || ticket.createdAt)}
        </span>
      </div>
      <div className="flex gap-2">
        {ticket.customer && (
          <TicketAvatar
            className="self-start mt-2"
            user={ticket.customer}
            url={ticket.customer.avatarUrl}
          />
        )}
        <div className="w-full">
          <div className="flex gap-2 items-center justify-between">
            <p style={theme.textVariants.base} className="line-clamp-1">
              {getUserFullName(ticket.customer)}
            </p>
          </div>
          <p style={theme.textVariants.baseBold} className="line-clamp-1">
            {ticket.title}
          </p>
          <div className="line-clamp-1">{message}</div>
          <div className="flex flex-col  justify-between min-w-fit mt-2">
            <div className="flex gap-2 justify-end items-center">
              {ticket.assignedUser?.id && (
                <TicketAvatar
                  size={24}
                  user={ticket.assignedUser}
                  url={ticket.assignedUser.avatarUrl}
                />
              )}
              <TicketVariantBadge variant={ticket.variant} />
              <TicketBadge status={ticket.status} />
              {ticket.unreadMessages > 0 && (
                <NotificationBadge show absoluteOffset={false} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

export const filter = (
  variant: 'all' | 'my' | 'open',
  userId: string
): TicketFilterBoard => {
  if (variant === 'all') {
    return {
      status: ['open', 'closed'],
    }
  }
  if (variant === 'open') {
    return {
      status: ['open'],
    }
  }
  return {
    assignedIds: userId,
    status: ['open'],
  }
}

export const TicketListViewBoard = ({
  variant,
}: {
  variant: 'all' | 'my' | 'open'
}): ReactNode => {
  const { ticketId } = useParams()
  const { society } = useCurrentSociety()
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const { data, isFetching, fetchNextPage, isLoading } = useInfiniteQuery(
    infiniteTicketQueries.ticketsBoard({
      societyId: society?._id,
      filter: filter(variant, userId!),
    })
  )
  const tickets = data?.pages.flatMap((page) => page.items || [])
  const isEmpty = !tickets?.length && !isLoading

  if (isEmpty) {
    return (
      <ErrorView
        title={translate('ticketListViewBoard.empty.title')}
        subtitle={translate('ticketListViewBoard.empty.subtitle', {
          context: variant,
        })}
        illustration={IllustrationChoices.EMPTY}
        className="mt-14"
      />
    )
  }

  return (
    <InfiniteScroll
      className="mt-4"
      onEndReached={() => fetchNextPage({ cancelRefetch: false })}
      isFetching={isFetching}
    >
      {tickets?.map((ticket) => {
        return (
          <TicketListItemBoard
            active={ticket.id === ticketId}
            key={ticket.id}
            ticket={ticket}
          />
        )
      })}
    </InfiniteScroll>
  )
}
